import type { CrawlResult } from './types';
import type { ConvexActionClient } from './convexTypes';
import { api } from '../../convex/_generated/api';

export class FirecrawlClient {
  private apiKey: string;
  private convexClient: ConvexActionClient;

  constructor(apiKey: string, convexClient: ConvexActionClient) {
    this.apiKey = apiKey;
    this.convexClient = convexClient;
  }

  /**
   * Scrape a single URL and extract content
   */
  async scrape(url: string): Promise<CrawlResult | null> {
    try {
      const result = await this.convexClient.action(api.firecrawl.scrapeUrl, {
        url,
        apiKey: this.apiKey,
      });

      return result as CrawlResult | null;
    } catch (error: any) {
      console.error(`❌ Firecrawl error for ${url}:`, error.message);
      return null;
    }
  }

  /**
   * Scrape multiple URLs in parallel with rate limiting
   */
  async scrapeMultiple(
    urls: string[],
    options?: { concurrency?: number }
  ): Promise<CrawlResult[]> {
    try {
      const results = await this.convexClient.action(api.firecrawl.scrapeMultiple, {
        urls,
        apiKey: this.apiKey,
        concurrency: options?.concurrency,
      });

      return results as CrawlResult[];
    } catch (error: any) {
      console.error(`❌ Firecrawl batch error:`, error.message);
      return [];
    }
  }

  /**
   * Search for content using Firecrawl search
   */
  async search(query: string, limit?: number): Promise<CrawlResult[]> {
    try {
      const results = await this.convexClient.action(api.firecrawl.searchQuery, {
        query,
        apiKey: this.apiKey,
        limit,
      });

      return results as CrawlResult[];
    } catch (error: any) {
      console.error(`❌ Firecrawl search error for "${query}":`, error.message);
      return [];
    }
  }

  /**
   * Search multiple queries using Firecrawl search
   */
  async searchMultiple(queries: string[], limit?: number): Promise<CrawlResult[]> {
    try {
      const results = await this.convexClient.action(api.firecrawl.searchMultiple, {
        queries,
        apiKey: this.apiKey,
        limit,
      });

      return results as CrawlResult[];
    } catch (error: any) {
      console.error(`❌ Firecrawl search batch error:`, error.message);
      return [];
    }
  }

  /**
   * Extract video player elements from HTML
   */
  static detectVideoPlayers(html: string): string[] {
    const players: string[] = [];
    const lowercaseHtml = html.toLowerCase();

    // Common video player patterns
    const patterns = [
      { name: 'HTML5 Video', pattern: /<video[\s>]/i },
      { name: 'JW Player', pattern: /jwplayer/i },
      { name: 'Video.js', pattern: /videojs|video-js/i },
      { name: 'Plyr', pattern: /plyr/i },
      { name: 'Flowplayer', pattern: /flowplayer/i },
      { name: 'Dailymotion', pattern: /dailymotion\.com\/embed/i },
      { name: 'Vimeo', pattern: /player\.vimeo\.com/i },
      { name: 'YouTube', pattern: /youtube\.com\/embed/i },
      { name: 'Iframe Player', pattern: /<iframe[^>]+src=["'][^"']*\.(mp4|m3u8)/i },
    ];

    for (const { name, pattern } of patterns) {
      if (pattern.test(html)) {
        players.push(name);
      }
    }

    return players;
  }

  /**
   * Extract download links from content
   */
  static extractDownloadLinks(links: string[], html: string): string[] {
    const downloadLinks: string[] = [];

    // Check direct file links
    const videoExtensions = ['.mp4', '.mkv', '.avi', '.mov', '.wmv', '.flv', '.torrent'];
    for (const link of links) {
      if (videoExtensions.some(ext => link.toLowerCase().endsWith(ext))) {
        downloadLinks.push(link);
      }
    }

    // Check for download buttons/links in HTML
    const downloadPatterns = [
      /href=["']([^"']+)["'][^>]*>(download|تحميل)/i,
      /download[^>]*href=["']([^"']+)["']/i,
    ];

    for (const pattern of downloadPatterns) {
      const matches = html.matchAll(new RegExp(pattern, 'gi'));
      for (const match of matches) {
        if (match[1] && !downloadLinks.includes(match[1])) {
          downloadLinks.push(match[1]);
        }
      }
    }

    return downloadLinks;
  }
}
